import { useMemo } from "react";
import { Card, CardBody, CardTitle, Col } from "reactstrap";
import {
  CONTROL_MAPPING_TYPE_COLORS,
  CONTROL_MAPPING_TYPE_ORDER,
} from "src/modules/data-protection/constants/Common";
import {
  resolveFrameworkMatrixSources,
  resolveFrameworkMatrixTarget,
} from "src/modules/data-protection/helpers/Controls";

const MAPPING_TYPE_LABELS = {
  direct: "Direct",
  partial: "Partial",
  conceptual: "Conceptual",
  related: "Related",
};

const getMappingTypeCount = (mappingsCount, percent) =>
  Math.round((percent / 100) * mappingsCount);

const getCoveragePercent = (row) => {
  const total =
    Number(row.direct_percent ?? 0) +
    Number(row.partial_percent ?? 0) +
    Number(row.conceptual_percent ?? 0) +
    Number(row.related_percent ?? 0);

  return Math.min(100, Math.round(total));
};

const FrameworkMappingMatrixSection = ({ t, dashboardData, frameworkLabelMap }) => {
  const matrixRows = dashboardData?.framework_matrix || [];

  const targetFramework = useMemo(
    () => resolveFrameworkMatrixTarget(dashboardData, matrixRows),
    [dashboardData, matrixRows]
  );

  const sourceFrameworks = useMemo(
    () => resolveFrameworkMatrixSources(dashboardData, matrixRows, targetFramework),
    [dashboardData, matrixRows, targetFramework]
  );

  const matrixBySource = useMemo(() => {
    const map = new Map();

    if (!targetFramework) {
      return map;
    }

    matrixRows.forEach((row) => {
      if (
        row.target_framework === targetFramework &&
        row.source_framework &&
        row.source_framework !== targetFramework
      ) {
        map.set(row.source_framework, row);
      }
    });

    return map;
  }, [matrixRows, targetFramework]);

  const hasMatrix = Boolean(targetFramework) && sourceFrameworks.length > 0;
  const targetLabel =
    frameworkLabelMap?.[targetFramework] || targetFramework || "";

  return (
    <Col lg="8" md="12" className="mb-3 d-flex">
      <Card className="card_box_Shadow h-100 w-100">
        <CardBody>
          <CardTitle>
            {t("Framework Mapping to {{framework}} Matrix", {
              framework: targetLabel,
            })}
          </CardTitle>

          {!hasMatrix ? (
            <p className="text-muted mb-0 mt-2">{t("No mapping data available")}</p>
          ) : (
            <div className="framework-mapping-matrix-wrapper mt-2">
              <table className="framework-mapping-matrix-table">
                <thead>
                  <tr>
                    <th scope="col" className="framework-mapping-matrix-corner">
                      {t("Frameworks")}
                    </th>
                    {CONTROL_MAPPING_TYPE_ORDER.map((type) => (
                      <th
                        key={type}
                        scope="col"
                        className="framework-mapping-matrix-type-header"
                        style={{
                          backgroundColor: CONTROL_MAPPING_TYPE_COLORS[type],
                          color: "#212529",
                        }}
                      >
                        {t(MAPPING_TYPE_LABELS[type])}
                      </th>
                    ))}
                    <th scope="col" className="framework-mapping-matrix-metric-header">
                      {t("Coverage")}
                    </th>
                    <th scope="col" className="framework-mapping-matrix-metric-header">
                      {t("Total Mapped")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sourceFrameworks.map((sourceFramework) => {
                    const row = matrixBySource.get(sourceFramework);
                    const label =
                      frameworkLabelMap?.[sourceFramework] || sourceFramework;
                    const mappingsCount = Number(row?.mappings_count ?? 0);
                    const hasRowData = Boolean(row) && mappingsCount > 0;
                    const coverage = row ? getCoveragePercent(row) : null;

                    return (
                      <tr key={sourceFramework}>
                        <th scope="row" className="framework-mapping-matrix-row-label">
                          {label}
                        </th>

                        {CONTROL_MAPPING_TYPE_ORDER.map((type) => {
                          const percent = Math.round(Number(row?.[`${type}_percent`] ?? 0));
                          const hasValue = hasRowData && percent > 0;

                          if (!hasValue) {
                            return (
                              <td
                                key={type}
                                className="framework-mapping-matrix-type-cell framework-mapping-matrix-type-cell--empty"
                              >
                                —
                              </td>
                            );
                          }

                          const count = getMappingTypeCount(mappingsCount, percent);

                          return (
                            <td
                              key={type}
                              className="framework-mapping-matrix-type-cell framework-mapping-matrix-type-cell--filled"
                              style={{
                                backgroundColor: CONTROL_MAPPING_TYPE_COLORS[type],
                              }}
                            >
                              <span className="framework-mapping-matrix-type-count">
                                {count}
                              </span>
                              <span className="framework-mapping-matrix-type-percent">
                                {percent}%
                              </span>
                            </td>
                          );
                        })}

                        <td className="framework-mapping-matrix-metric-cell">
                          {hasRowData ? `${coverage}%` : "—"}
                        </td>
                        <td className="framework-mapping-matrix-metric-cell">
                          {hasRowData ? mappingsCount : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default FrameworkMappingMatrixSection;
